import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";

const TONE = {
  filed: "border-emerald-200 bg-emerald-50 text-emerald-800",
  overdue: "border-rose-300 bg-rose-50 text-rose-800",
  due_soon: "border-amber-300 bg-amber-50 text-amber-900",
  upcoming: "border-[#E7E2D6] bg-white text-foreground",
};
const monthName = (p) => new Date(`${p}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" });

const label = (r) => r.filed ? `Filed ${fmtDateTime(r.filed_at)} by ${r.filed_by}`
  : r.days_left < 0 ? `Overdue by ${-r.days_left} day${r.days_left === -1 ? "" : "s"}. Late fee and interest apply`
  : r.days_left === 0 ? "Due today" : `${r.days_left} day${r.days_left === 1 ? "" : "s"} left`;

const ReturnCard = ({ r, onChange }) => {
  const toggle = async () => {
    try {
      if (r.filed) await api.delete("/gst/calendar/filed", { params: { form: r.form, period: r.period } });
      else await api.post("/gst/calendar/filed", { form: r.form, period: r.period });
      toast.success(r.filed ? "Marked as not filed" : `${r.form} marked as filed`); onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const Icon = r.filed ? CheckCircle2 : r.status === "upcoming" ? CalendarClock : AlertTriangle;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-2xl border p-4 ${TONE[r.status]}`} data-testid={`gst-return-${r.form}`}>
      <Icon className="h-6 w-6 shrink-0" />
      <div className="min-w-[180px] flex-1">
        <div className="font-semibold">{r.form} for {monthName(r.period)} · due {fmtDate(r.due)}</div>
        <div className="text-xs opacity-80">{r.what}</div>
        <div className="mt-0.5 text-sm font-medium" data-testid={`gst-return-${r.form}-status`}>{label(r)}</div>
      </div>
      <Button size="sm" variant={r.filed ? "ghost" : "outline"} onClick={toggle} className="bg-white/60" data-testid={`gst-return-${r.form}-toggle`}>
        {r.filed ? <><Undo2 className="mr-1 h-4 w-4" />Undo</> : <><CheckCircle2 className="mr-1 h-4 w-4" />Mark filed</>}
      </Button>
    </div>
  );
};

export const GstCalendar = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/gst/calendar").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="gst-calendar">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">Returns due this month (monthly filer)</div>
      <div className="grid gap-3 lg:grid-cols-2">{data.returns.map((r) => <ReturnCard key={r.form} r={r} onChange={load} />)}</div>
    </div>
  );
};
